import styled, { keyframes } from 'styled-components'
import { Box } from '@/components/atoms/Grid'
import { validFor } from '@/theme/validFor'
import useTranslation from 'next-translate/useTranslation'
import { useTodayCountdown } from '../../hooks/useDailyCountdown'

const FlipUnit = ({ label, value }: { label: string; value: number }) => {
  const paddedValue =
    label === 'Days' ? value.toString() : value.toString().padStart(2, '0')

  return (
    <UnitContainer>
      <Card>
        {/* The server and the browser render a few seconds apart, the next
            tick replaces the server text */}
        <CardInner key={paddedValue}>
          <CardFace suppressHydrationWarning>{paddedValue}</CardFace>
          <CardBack suppressHydrationWarning>{paddedValue}</CardBack>
        </CardInner>
      </Card>
      <Label suppressHydrationWarning>{label}</Label>
    </UnitContainer>
  )
}

const CampaignCounter = (): JSX.Element => {
  const { days, hours, minutes, seconds } = useTodayCountdown()
  const { t } = useTranslation('common')
  const capitalizeLetter = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
  const getTimeLabel = (unit: number, keyPrefix: string) => {
    if (unit === 0) {
      return t(`${keyPrefix}.zero`)
    }
    if (unit === 1) {
      return t(`${keyPrefix}.one`)
    }
    if (unit >= 2 && unit <= 4) {
      return t(`${keyPrefix}.few`)
    }
    return t(`${keyPrefix}.other`)
  }

  return (
    <CountdownWrapper>
      <CountdownInner>
        <FlipUnit
          label={capitalizeLetter(getTimeLabel(days, 'Time.days'))}
          value={days}
        />
        <FlipUnit
          label={capitalizeLetter(getTimeLabel(hours, 'Time.hours'))}
          value={hours}
        />
        <FlipUnit
          label={capitalizeLetter(getTimeLabel(minutes, 'Time.minutes'))}
          value={minutes}
        />
        <FlipUnit
          label={capitalizeLetter(getTimeLabel(seconds, 'Time.seconds'))}
          value={seconds}
        />
      </CountdownInner>
    </CountdownWrapper>
  )
}

export default CampaignCounter

const CountdownWrapper = styled(Box)`
  width: 100%;
  display: flex;
  justify-content: center;
`

const CountdownInner = styled(Box)`
  position: relative;
  right: 80px;
  display: flex;
  gap: 24px;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  ${validFor.tablet`
     right: 0px;
     gap: 16px;
  `}
  ${validFor.mobile`
    gap: 16px;
  `}
`
const UnitContainer = styled(Box)`
  text-align: center;
`

const Card = styled(Box)`
  perspective: 3000px;
`

const flip = keyframes`
  0% { transform: rotateX(0); }
  100% { transform: rotateX(180deg); }
`

const CardInner = styled(Box)`
  position: relative;
  width: 60px;
  height: 70px;
  transform-style: preserve-3d;
  animation: ${flip} 0.3s linear;
  ${validFor.tablet`
      width: 40px;
      height: 45px;
  `}
  ${validFor.mobile`
      width: 40px;
      height: 45px;
  `}
`

const CardFace = styled(Box)`
  position: absolute;
  width: 100%;
  height: 100%;
  font-size: 30px;
  font-weight: bold;
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.onBackground};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  backface-visibility: hidden;
  ${validFor.tablet`
     border-radius: 4px;
     font-size: 20px;
  `}
  ${validFor.mobile`
     border-radius: 4px;
     font-size: 16px;
  `}
`

const CardBack = styled(CardFace)`
  transform: rotateX(180deg);
`

const Label = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 14px;
  font-weight: bold;
  color: ${(props) => props.theme.colors.background};
  ${validFor.tablet`
    font-size: 10px;
    margin-top: 0px;
  `}
  ${validFor.mobile`
    font-size: 10px;
    margin-top: 0px;
  `}
`
